// @flow

/**
 * Video player wrapper class, which interfaces directly with HlsVideo or Gestalt Video
 * 08/16/19 - Will be working to consolidate all of the different video players on
 * our webapp; this should be seen moving forward as the Video Player to use, so we
 * can remain consistent in our rendering of Video / HlsVideo
 */
import React, { type ElementConfig, type ElementRef } from 'react';
import BrowserDetector from 'app/common/lib/BrowserDetector';
import HlsVideo from 'app/common/react/components/video/HlsVideo';
import { Video } from 'gestalt';
import type { HlsConfig, Source } from 'app/packages/pinterest-flow-types/video';

type Props = {|
  ...ElementConfig<typeof Video>,
  hlsConfig?: HlsConfig,
  videoStartTime?: ?number,
|};

type State = {|
  useHlsVideo: boolean,
|};

/*
 * Helper to get the m3u8 playlists of the src.
 */
export const getPlaylistSourceSrc = (src: Source) => {
  if (Array.isArray(src)) {
    const hlsSource = src.find((source) => source.type === 'video/m3u8');
    return hlsSource ? hlsSource.src : undefined;
  }

  return RegExp('m3u8$').test(src) ? src : undefined;
};

export default class VideoWrapper extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const hlsPlaylistSource = getPlaylistSourceSrc(props.src);

    this.state = {
      // Due to differences in video player and HLS playback capabilities, we need to
      // have a separate treatment for Safari.
      useHlsVideo: !BrowserDetector.isSafari() && !!hlsPlaylistSource,
    };
  }

  videoPlayerRef: ElementRef<typeof Video> | null;

  // Only set when we are not using HlsVideo (as HlsVideo is another wrapper around Video)
  setVideoPlayerRef = (ref: ElementRef<typeof Video> | null): void => {
    if (ref) {
      this.videoPlayerRef = ref;
    }
  };

  handleOnEnded = ({ event }: { event: SyntheticEvent<HTMLVideoElement> }) => {
    const { loop, onEnded } = this.props;
    if (onEnded) {
      onEnded({ event });

      // Manually loop the video if the loop prop is set && video ref is set properly
      if (this.videoPlayerRef && this.videoPlayerRef.video && loop) {
        this.videoPlayerRef.video.play();
      }
    }
  };

  handleOnReady = ({ event }: { event: SyntheticEvent<HTMLVideoElement> }) => {
    const { onReady } = this.props;
    let { videoStartTime } = this.props;
    const { useHlsVideo } = this.state;

    if (videoStartTime === null || videoStartTime === undefined) {
      videoStartTime = 0;
    }

    if (videoStartTime > 0 && this.videoPlayerRef && this.videoPlayerRef.video && !useHlsVideo) {
      this.videoPlayerRef.video.currentTime = videoStartTime;
    }

    if (onReady) {
      onReady({ event });
    }
  };

  render() {
    const { hlsConfig, loop, onEnded, src, videoStartTime, ...videoProps } = this.props;
    const { useHlsVideo } = this.state;
    const hlsPlaylistSource = getPlaylistSourceSrc(src);

    const updatedHlsConfig = { ...hlsConfig, startPosition: videoStartTime || -1 };

    // if onEnded exists as a callback, we want to manually loop the video if loop is set (otherwise it's difficult
    // to note when the video is ended, for logging purposes)
    const loopOverride = onEnded ? false : loop;

    return useHlsVideo ? (
      <HlsVideo
        {...videoProps}
        hlsConfig={updatedHlsConfig}
        loop={loopOverride}
        onEnded={this.handleOnEnded}
        setVideoRef={this.setVideoPlayerRef}
        src={hlsPlaylistSource}
      />
    ) : (
      <Video
        {...videoProps}
        loop={loopOverride}
        onEnded={this.handleOnEnded}
        onReady={this.handleOnReady}
        ref={this.setVideoPlayerRef}
        src={hlsPlaylistSource || src}
      />
    );
  }
}
